/**
 * `/integration` — the Integration section landing.
 *
 * Three tiles: the data store, the connectors that fill it and the agents that
 * read it. The page exists to make one argument, which is the demo's own: any
 * system with an export, an API or a mailbox can be a source, and nothing a
 * customer already runs has to be replaced for that to be true.
 *
 * ## Source
 *
 * The demo had no Integration landing — `show('section','integration')`
 * rendered a generic section hero (demo line 1994 / 2994) whose tiles were the
 * three views below. The standfirsts on the tiles are those views' own
 * standfirsts, verbatim (demo lines 1451–1453).
 *
 * ## Data
 *
 * Eager only — the connector and agent seeds plus the lake file count from
 * `oem-counts.ts`. Nothing is fetched.
 *
 * @see src/versions/v1/modules/integration/IntegrationSectionScene.transform.ts
 * @see docs/logic/integration/overview.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ChevronRight, Database, Link2, type LucideIcon } from 'lucide-react';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import {
  ANY_SYSTEM_NOTE,
  FORMATS_NOTE,
  integrationCards,
  type IntegrationCard,
} from './IntegrationSectionScene.transform';

const CARD_ICON: Readonly<Record<IntegrationCard['id'], LucideIcon>> = {
  data: Database,
  connectors: Link2,
  agents: Bot,
};

/** The section standfirst — the demo's own framing of what Integration is. */
const STANDFIRST =
  'What the workspace is built on and what it reads. Each system keeps producing what it ' +
  'already produces; one store holds it in that format and cross-references it; sixteen agents ' +
  'read the store. Nothing is replaced.';

export function IntegrationSectionScene() {
  const cards = useMemo(() => integrationCards(), []);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="IntegrationSectionScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Integration"
            title="Integration"
            description={STANDFIRST}
            actions={<HelpAnchor doc="integration/overview" />}
          />

          <AskStrip view="data" />

          <Commentable name="IntegrationCards">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-3">
              {cards.map((card) => {
                const Icon = CARD_ICON[card.id];
                return (
                  <Link
                    key={card.id}
                    to={card.to}
                    className="group flex min-w-0 flex-col gap-2.5 rounded-xl border p-4 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <span className="flex items-center gap-2.5">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: 'var(--surface-3)', color: 'var(--accent)' }}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.7} />
                      </span>
                      <span
                        className="text-[15px] font-semibold tracking-tight"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {card.title}
                      </span>
                      <ChevronRight
                        className="ml-auto h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                        style={{ color: 'var(--ink-tertiary)' }}
                        aria-hidden="true"
                      />
                    </span>

                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <span
                        className="mono text-[26px] font-semibold leading-none tracking-tight"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {card.count.toLocaleString('en-AU')}
                      </span>
                      <span className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
                        {card.unit}
                      </span>
                    </span>

                    <span
                      className="text-[12.5px] leading-[1.5]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {card.blurb}
                    </span>
                    <span
                      className="text-[11.5px] leading-[1.5]"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {card.detail}
                    </span>
                  </Link>
                );
              })}
            </div>
          </Commentable>

          <Commentable name="IntegrationFormats">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <p
                className="text-[13.5px] font-medium leading-[1.55]"
                style={{ color: 'var(--ink-primary)' }}
              >
                {ANY_SYSTEM_NOTE}
              </p>
              <p
                className="mt-2 text-[11.5px] leading-[1.6]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                {FORMATS_NOTE}
              </p>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
